import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { notificationService } from './notificationService';
import { NotificationMessage, NotificationOptions } from './types';
import { NotificationSnackbar } from './NotificationSnackbar';

interface NotificationContextType {
  addNotification: (message: string, options?: NotificationOptions) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  info: (message: string, options?: Omit<NotificationOptions, 'level'>) => string;
  success: (message: string, options?: Omit<NotificationOptions, 'level'>) => string;
  warning: (message: string, options?: Omit<NotificationOptions, 'level'>) => string;
  error: (message: string, options?: Omit<NotificationOptions, 'level'>) => string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationMessage[]>([]);

  useEffect(() => {
    const unsubscribe = notificationService.subscribe(setNotifications);
    return unsubscribe;
  }, []);

  const contextValue: NotificationContextType = {
    addNotification: notificationService.addNotification.bind(notificationService),
    removeNotification: notificationService.removeNotification.bind(notificationService),
    clearAll: notificationService.clearAll.bind(notificationService),
    info: notificationService.info.bind(notificationService),
    success: notificationService.success.bind(notificationService),
    warning: notificationService.warning.bind(notificationService),
    error: notificationService.error.bind(notificationService),
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {/* Render all active notifications */}
      {notifications.map((notification) => (
        <NotificationSnackbar
          key={notification.id}
          notification={notification}
          onClose={notificationService.removeNotification.bind(notificationService)}
        />
      ))}
    </NotificationContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook must be co-located with its context provider
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
